import { endpoints, baseurl } from "../Utils/Constants";

export const sendAegisAiChatMessage = async (token, payload) => {
    try {
        const response = await fetch(baseurl + endpoints.AEGIS_AI_CHAT_SEND_MESSAGE, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        return await response.json();
    } catch (error) {
        console.error("Error sending Aegis AI chat message:", error);
        throw error;
    }
};

// Ends a conversation's server-side memory right away (it also expires on its own after idle time).
// The Teams tab passes its own endpoint, authenticated with the Teams sign-on token.
export const clearAegisAiChatSession = async (token, sessionId, endpoint = endpoints.AEGIS_AI_CHAT_CLEAR_SESSION) => {
    try {
        const response = await fetch(baseurl + endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ session_id: sessionId }),
            keepalive: true,
        });
        return await response.json();
    } catch (error) {
        console.error("Error clearing Aegis AI chat session:", error);
        return null;
    }
};

// Pass an AbortSignal to cancel; aborting is silent (no onError). The Teams tab passes its own stream endpoint.
export const streamAegisAiChatMessage = async (token, payload, { onToken, onSources, onThinking, onImages, onDone, onError }, signal, endpoint) => {
    try {
        const streamEndpoint = endpoint || endpoints.AEGIS_AI_CHAT_SEND_MESSAGE_STREAM || "/aegisai/chat/sendMessageStream";
        const response = await fetch(baseurl + streamEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload),
            signal,
        });

        if (!response.ok) {
            const errJson = await response.json().catch(() => null);
            throw new Error(errJson?.message || `Server responded with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            for (const part of parts) {
                const trimmed = part.trim();
                if (!trimmed) continue;
                for (const line of trimmed.split("\n")) {
                    if (line.startsWith("data: ")) {
                        const jsonStr = line.slice(6).trim();
                        if (jsonStr === "[DONE]") {
                            onDone && onDone();
                            return;
                        }
                        try {
                            const parsed = JSON.parse(jsonStr);
                            if (parsed.type === "token") {
                                onToken && onToken(parsed.content);
                            } else if (parsed.type === "thinking") {
                                onThinking && onThinking(parsed.message || parsed.content);
                            } else if (parsed.type === "sources") {
                                onSources && onSources(parsed.sources);
                            } else if (parsed.type === "images") {
                                onImages && onImages(parsed.images);
                            } else if (parsed.type === "done") {
                                onDone && onDone();
                                return;
                            } else if (parsed.type === "error") {
                                onError && onError(parsed.message || "Streaming error");
                                return;
                            }
                        } catch (e) {
                            console.warn("Failed to parse SSE line:", line, e);
                        }
                    }
                }
            }
        }
        onDone && onDone();
    } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error in streamAegisAiChatMessage:", error);
        onError && onError(error.message || "Failed to stream message");
    }
};
